/**
 * Color Contrast Context Provider
 * Handles color contrast validation and accessibility color utilities
 */

/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useCallback } from 'react';
import { 
  calculateContrastRatio, 
  validateContrast, 
  getAccessibleColor 
} from '@/lib/color-contrast';

interface ColorContrast {
  validateContrast: (foreground: string, background: string) => boolean;
  getAccessibleColor: (color: string, background: string) => string;
  calculateContrastRatio: (foreground: string, background: string) => number;
}

const ColorContrastContext = createContext<ColorContrast | undefined>(undefined);

export const useColorContrast = () => {
  const context = useContext(ColorContrastContext);
  if (context === undefined) {
    throw new Error('useColorContrast must be used within a ColorContrastProvider');
  }
  return context;
};

interface ColorContrastProviderProps {
  children: React.ReactNode;
}

export const ColorContrastProvider: React.FC<ColorContrastProviderProps> = ({ children }) => {
  const validateContrastRatio = useCallback((foreground: string, background: string): boolean => {
    return validateContrast(foreground, background);
  }, []);

  const getAccessibleColorVariant = useCallback((color: string, background: string): string => {
    return getAccessibleColor(color, background);
  }, []);

  const calculateRatio = useCallback((foreground: string, background: string): number => {
    return calculateContrastRatio(foreground, background);
  }, []);

  const contextValue: ColorContrast = {
    validateContrast: validateContrastRatio,
    getAccessibleColor: getAccessibleColorVariant,
    calculateContrastRatio: calculateRatio,
  };

  return (
    <ColorContrastContext.Provider value={contextValue}>
      {children}
    </ColorContrastContext.Provider>
  );
};